import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * Painel "Saude do servico" (ADR 0067). Quem decide se a pessoa e admin e a
 * API, a cada requisicao: esconder o modulo aqui e so para nao mostrar um
 * botao que responderia 403.
 */

export type MotivoDeErro = 'sem_conexao' | 'credencial';

export interface SaudeDoServico {
  geradoEm: string;
  servico: {
    versao: string;
    node: string;
    uptimeSegundos: number;
    migracoes: { aplicadas: number | null; conhecidas: number; ultima: string | null };
  };
  contas: {
    total: number;
    porEstado: Record<'em_dia' | 'sincronizando' | MotivoDeErro, number>;
    nuncaSincronizadas: number;
    ultimaSincronizacao: string | null;
    maisAtrasada: { conta: string; servidor: string; ultimaSincronizacao: string } | null;
    porServidor: { dominio: string; total: number; comErro: number }[];
    comErro: {
      conta: string;
      servidor: string;
      motivo: MotivoDeErro;
      falhas: number;
      ultimaSincronizacao: string | null;
    }[];
  };
  envio: {
    pendentes: number;
    agendadas: number;
    presas: number;
    falhasUltimos7Dias: number;
    tentativasMaximas: number;
    maisAntigaDesde: string | null;
  };
  sync: {
    intervaloMs: number;
    pollingComIdleMs: number;
    idle: {
      teto: number;
      tetoPorHost: number;
      vigiando: number;
      abrindo: number;
      emEspera: number;
      semIdle: number;
    } | null;
  } | null;
  entrada: {
    periodos: {
      periodo: '24h' | '7d' | '30d';
      loginsOk: number;
      recusasPorServidor: number;
      bloqueios: number;
      cadastrosRecusados: number;
    }[];
  };
  uso: {
    usuarios: number;
    ativos7Dias: number;
    ativos30Dias: number;
    porDia: { dia: string; enviadas: number; recebidas: number }[];
  };
  lembretes: { pendentes: number; vencidos: number };
  tarefas: { pendentes: number; vencidas: number; concluidas: number };
}

/** Comeca com `admin`: o cache em disco deixa estas chaves de fora (CachePersistido). */
export const CHAVE_ADMIN = 'admin';

function usePapel() {
  return useQuery({
    queryKey: [CHAVE_ADMIN, 'papel'],
    queryFn: () => api<{ papel: 'master' | null; totpPendente?: true }>('/auth/papel'),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/**
 * Mostra o modulo Admin: `true` quando a API confirma o papel ou quando a pessoa
 * esta na lista e falta o TOTP (ADR 0068), para ela ver o aviso. `null`, erro ou
 * sem resposta: `false`.
 */
export function useEhAdmin(): boolean {
  const papel = usePapel().data;
  return papel?.papel === 'master' || papel?.totpPendente === true;
}

/**
 * Na lista de admins, mas esta sessao nao passou pelo TOTP. `undefined`
 * enquanto a API nao respondeu; erro conta como `false`.
 */
export function useTotpPendente(): boolean | undefined {
  const papel = usePapel();
  return papel.isPending ? undefined : papel.data?.totpPendente === true;
}

/** `ativo: false` nao pergunta: sem TOTP a resposta seria 403 a cada 30 s. */
export function useSaudeDoServico(ativo: boolean) {
  return useQuery({
    queryKey: [CHAVE_ADMIN, 'saude'],
    queryFn: () => api<SaudeDoServico>('/admin/saude'),
    enabled: ativo,
    retry: false,
    refetchInterval: 30_000,
    // Aba escondida nao precisa de painel atualizado.
    refetchIntervalInBackground: false,
  });
}

/** "ha 3 min", "ha 2 h", "ha 4 dias". */
export function haQuanto(isoData: string | null, agora = Date.now()): string {
  if (isoData === null) return 'nunca';
  const s = Math.max(0, Math.round((agora - new Date(isoData).getTime()) / 1000));
  if (s < 60) return 'agora';
  if (s < 3600) return `há ${Math.floor(s / 60)} min`;
  if (s < 86_400) return `há ${Math.floor(s / 3600)} h`;
  const d = Math.floor(s / 86_400);
  return `há ${d} ${d === 1 ? 'dia' : 'dias'}`;
}

/** Tempo ligado em texto curto: "3 d 4 h", "2 h 10 min". */
export function duracao(segundos: number): string {
  const d = Math.floor(segundos / 86_400);
  const h = Math.floor((segundos % 86_400) / 3600);
  const m = Math.floor((segundos % 3600) / 60);
  if (d > 0) return `${d} d ${h} h`;
  if (h > 0) return `${h} h ${m} min`;
  return `${m} min`;
}
